// Firebase Storage service for JIMI Presentation
// This is a separate service from the main app

import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  listAll, 
  deleteObject,
  getMetadata
} from 'firebase/storage';
import { storage } from './firebase';  // Use local Firebase instance

// Constants
const DEBUG_ENABLED = import.meta.env.VITE_DEBUG_FIREBASE === 'true';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const LOGO_PATH = 'logo';
const VIDEO_PATH = 'videos';
const IMAGE_PATH = 'images';
const AUDIO_PATH = 'audio';

// Upload a video to Firebase Storage
export const uploadVideo = (file: File, path: string = VIDEO_PATH, onProgress?: (progress: number) => void) => {
  return new Promise<string>((resolve, reject) => {
    const storageRef = ref(storage, `${path}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        console.error('Error uploading video:', error);
        reject(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            resolve(downloadURL);
          })
          .catch((error) => {
            console.error('Error getting download URL:', error);
            reject(error);
          });
      }
    );
  });
};

// Upload an image to Firebase Storage
export const uploadImage = (file: File, path: string = IMAGE_PATH, onProgress?: (progress: number) => void) => {
  return new Promise<string>((resolve, reject) => {
    const storageRef = ref(storage, `${path}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        console.error('Error uploading image:', error);
        reject(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            resolve(downloadURL);
          })
          .catch((error) => {
            console.error('Error getting download URL:', error);
            reject(error);
          });
      }
    );
  });
};

// Get the download URL for a video
export const getVideoUrl = async (videoPath: string): Promise<string> => {
  try {
    const storageRef = ref(storage, videoPath);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting video URL:', error);
    throw error;
  }
};

// Get the download URL for an image
export const getImageUrl = async (imagePath: string): Promise<string> => {
  try {
    const storageRef = ref(storage, imagePath);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting image URL:', error);
    throw error;
  }
};

// List all videos in the videos folder
export const listVideos = async (): Promise<{ name: string; url: string }[]> => {
  try {
    const videosRef = ref(storage, VIDEO_PATH);
    const result = await listAll(videosRef);
    
    const videos = await Promise.all(
      result.items.map(async (itemRef) => {
        try {
          const url = await getDownloadURL(itemRef);
          return {
            name: itemRef.name,
            url
          };
        } catch (error) {
          console.error(`Error getting URL for ${itemRef.name}:`, error);
          return {
            name: itemRef.name,
            url: ''
          };
        }
      })
    );
    
    return videos;
  } catch (error) {
    console.error('Error listing videos:', error);
    return [];
  }
};

// Delete a video from Firebase Storage
export const deleteVideo = async (videoPath: string): Promise<void> => {
  try {
    const storageRef = ref(storage, videoPath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
};

// Upload a logo image to Firebase Storage
export const uploadLogo = (file: File, path: string = LOGO_PATH, onProgress?: (progress: number) => void) => {
  return new Promise<string>((resolve, reject) => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Please upload an image file.');
      }

      // Validate file size (5MB max)
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('File too large. Maximum size is 5MB.');
      }

      if (DEBUG_ENABLED) {
        console.log('[LOGO UPLOAD] Starting logo upload:', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          path: path
        });
      }

      // Always use a consistent filename with timestamp to prevent caching
      const timestamp = Date.now();
      const storageRef = ref(storage, `${path}/houston-methodist-logo-${timestamp}.png`);
      if (DEBUG_ENABLED) {
        console.log('[LOGO UPLOAD] Created storage reference:', storageRef.fullPath);
      }

      // Add CORS and caching metadata
      const metadata = {
        contentType: file.type,
        cacheControl: 'no-cache, no-store, must-revalidate',
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'uploadTime': new Date().toISOString()
        }
      };

      // Create the upload task
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      if (DEBUG_ENABLED) {
        console.log('[LOGO UPLOAD] Created upload task');
      }

      // Monitor upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (DEBUG_ENABLED) {
            console.log(`[LOGO UPLOAD] Progress: ${progress}%`);
          }
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          console.error('[LOGO UPLOAD] Error during upload:', error);
          reject(new Error('Failed to upload logo. Please try again.'));
        },
        async () => {
          try {
            // Upload completed successfully
            if (DEBUG_ENABLED) {
              console.log('[LOGO UPLOAD] Upload completed successfully');
            }
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            if (DEBUG_ENABLED) {
              console.log('[LOGO UPLOAD] Got download URL:', downloadURL);
            }

            // Clean up old logo files
            try {
              const logoRef = ref(storage, path);
              const logoFiles = await listAll(logoRef);
              const currentFile = uploadTask.snapshot.ref.name;
              
              // Delete all logo files except the current one
              const deletePromises = logoFiles.items
                .filter(item => item.name !== currentFile)
                .map(item => deleteObject(item));
              
              await Promise.all(deletePromises);
              if (DEBUG_ENABLED) {
                console.log('[LOGO UPLOAD] Cleaned up old logo files');
              }
            } catch (cleanupError) {
              console.warn('[LOGO UPLOAD] Failed to clean up old files:', cleanupError);
              // Don't reject here, just log the warning
            }

            // Add timestamp to force cache refresh
            const urlWithTimestamp = `${downloadURL}?t=${timestamp}`;
            if (DEBUG_ENABLED) {
              console.log('[LOGO UPLOAD] URL with timestamp:', urlWithTimestamp);
            }
            
            resolve(urlWithTimestamp);
          } catch (error) {
            console.error('[LOGO UPLOAD] Error getting download URL:', error);
            reject(new Error('Failed to get logo URL. Please try again.'));
          }
        }
      );
    } catch (error) {
      console.error('[LOGO UPLOAD] Critical error:', error);
      reject(error instanceof Error ? error : new Error('Unknown error during logo upload'));
    }
  });
};

// Get the logo URL with cache busting
export const getLogoUrl = async (path: string = LOGO_PATH): Promise<string> => {
  try {
    if (DEBUG_ENABLED) {
      console.log('[LOGO] Getting latest logo from path:', path);
    }
    const logoRef = ref(storage, path);
    
    // List all files in the logo directory
    const logoFiles = await listAll(logoRef);
    
    if (logoFiles.items.length === 0) {
      throw new Error('No logo found');
    }
    
    // Sort by name (which includes timestamp) to get the latest
    const latestLogo = logoFiles.items
      .sort((a, b) => b.name.localeCompare(a.name))[0];
    
    const url = await getDownloadURL(latestLogo);
    const urlWithTimestamp = `${url}?t=${Date.now()}`;
    if (DEBUG_ENABLED) {
      console.log('[LOGO] Got latest logo URL:', urlWithTimestamp);
    }
    return urlWithTimestamp;
  } catch (error) {
    console.error('[LOGO] Error getting logo URL:', error);
    throw error instanceof Error ? error : new Error('Failed to get logo URL');
  }
};

// Check if a file exists in Firebase Storage
export const checkFileExists = async (path: string): Promise<boolean> => {
  try {
    const storageRef = ref(storage, path);
    await getMetadata(storageRef);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get the URL for a narration audio file
 * @param type The type of narration (slide or dashboard)
 * @param narrationId The ID of the narration
 * @returns Promise with the download URL
 */
export const getNarrationAudioUrl = async (type: 'slide' | 'dashboard', narrationId: string): Promise<string> => {
  try {
    const path = `narrations/${type}/${narrationId}.mp3`;
    console.log(`[Firebase] Attempting to fetch audio from path: ${path}`);
    
    // Check if file exists first
    const storageRef = ref(storage, path);
    console.log(`[Firebase] Created storage reference for: ${path}`);
    
    try {
      console.log(`[Firebase] Getting download URL for: ${path}`);
      const url = await getDownloadURL(storageRef);
      console.log(`[Firebase] Successfully retrieved URL: ${url}`);
      return url;
    } catch (downloadError) {
      console.error(`[Firebase] Error getting download URL for ${path}:`, downloadError);
      
      // If this is the user journey slide, try the numeric ID as fallback
      if (narrationId === 'user-journey') {
        const numericPath = `narrations/${type}/9.mp3`;
        console.log(`[Firebase] Trying fallback numeric path: ${numericPath}`);
        const fallbackRef = ref(storage, numericPath);
        
        try {
          const fallbackUrl = await getDownloadURL(fallbackRef);
          console.log(`[Firebase] Successfully retrieved fallback URL: ${fallbackUrl}`);
          return fallbackUrl;
        } catch (fallbackError) {
          console.error(`[Firebase] Error getting fallback URL for ${numericPath}:`, fallbackError);
          throw fallbackError;
        }
      }
      
      throw downloadError;
    }
  } catch (error) {
    console.error('[Firebase] Error getting audio URL:', error);
    // Return silent narration from new path structure
    console.log('[Firebase] Returning fallback silent audio');
    return '/narrations/slide/silent.mp3';
  }
};

// List all audio files in a specific folder
export const listAudioFiles = async (folderPath: string = AUDIO_PATH): Promise<{ name: string; url: string; slideId: string; timestamp: number }[]> => {
  try {
    const storageRef = ref(storage, folderPath);
    const result = await listAll(storageRef);
    
    const audioFiles = await Promise.all(
      result.items.map(async (itemRef) => {
        try {
          const url = await getDownloadURL(itemRef);
          
          // Extract slideId and timestamp from filename (format: slideId_timestamp.mp3)
          const filename = itemRef.name;
          const parts = filename.split('_');
          
          let slideId = 'unknown';
          let timestamp = 0;
          
          if (parts.length >= 2) {
            slideId = parts[0];
            // Extract timestamp from the second part (remove .mp3 extension)
            const timestampStr = parts[1].split('.')[0];
            timestamp = parseInt(timestampStr, 10) || 0;
          }
          
          return {
            name: itemRef.name,
            url,
            slideId,
            timestamp
          };
        } catch (error) {
          console.error(`Error getting download URL for ${itemRef.name}:`, error);
          return {
            name: itemRef.name,
            url: '',
            slideId: 'unknown',
            timestamp: 0
          };
        }
      })
    );
    
    // Sort by slideId and timestamp (newest first)
    return audioFiles.sort((a, b) => {
      if (a.slideId !== b.slideId) {
        return a.slideId.localeCompare(b.slideId);
      }
      return b.timestamp - a.timestamp;
    });
  } catch (error) {
    console.error('Error listing audio files:', error);
    return [];
  }
};

// Delete an audio file from Firebase Storage
export const deleteAudioFile = async (filePath: string): Promise<boolean> => {
  try {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
    console.log(`Successfully deleted audio file: ${filePath}`);
    return true;
  } catch (error) {
    console.error(`Error deleting audio file ${filePath}:`, error);
    return false;
  }
};

// Get the latest audio file for a specific slide
export const getLatestAudioFileForSlide = async (slideId: string): Promise<{ name: string; url: string; timestamp: number } | null> => {
  try {
    const audioFiles = await listAudioFiles();
    
    // Filter files for this slideId and sort by timestamp (newest first)
    const slideFiles = audioFiles
      .filter(file => file.slideId === slideId)
      .sort((a, b) => b.timestamp - a.timestamp);
    
    // Return the newest file or null if none found
    return slideFiles.length > 0 ? slideFiles[0] : null;
  } catch (error) {
    console.error(`Error getting latest audio file for slide ${slideId}:`, error);
    return null;
  }
};
